<template>
  <!-- A deliberate screen that opens over the service lane (norm 4's family): the design system's dialog,
       as the blocking notification is. Its title is always «manual order»; the step says the rest as the
       subtitle, and what scrolls is the body, with the buttons fixed under it. The dialog is `modal`
       (src/ui/layers.js); a tap on the scrim does not close it (it takes a code, and a mis-tap must not
       lose it): «Cancelar», back and Escape do. -->
  <BzDialog
    card
    :class="{ 'manual-order-dialog--wide': wideGrid }"
    :visible="show"
    band="modal"
    :title="$t('message.manualOrderTitle')"
    :subtitle="subtitle || undefined"
    data-testid="manual-order-card"
    @dismiss="onDismiss"
  >
    <div class="manual-order" data-testid="manual-order">
      <!-- Step 1: platform selection -->
      <template v-if="step === 'platform'">
        <!-- Loading -->
        <div v-if="loadingPlatforms" class="bz-empty">
          <BzIcon name="progress_activity" class="bz-empty__icon bz-spinner" />
          <p class="bz-empty__title">{{ $t('message.manualOrderLoadingPlatforms') }}</p>
        </div>

        <!-- Load error -->
        <div v-else-if="loadError" class="bz-empty">
          <BzIcon name="error" class="bz-empty__icon" />
          <p class="bz-empty__title">{{ $t('message.manualOrderLoadError') }}</p>
          <BzButton kind="secondary" size="md" :label="$t('message.manualOrderRetry')" @click="loadPlatforms" />
        </div>

        <!-- Empty (backend returned no platforms) -->
        <div v-else-if="!availablePlatforms.length" class="bz-empty">
          <BzIcon name="info" class="bz-empty__icon" />
          <p class="bz-empty__title">{{ $t('message.manualOrderNoPlatforms') }}</p>
        </div>

        <!-- Platforms -->
        <div v-else class="manual-order__platforms" :class="{ 'manual-order__platforms--wide': wideGrid }">
          <button
            v-for="platform in availablePlatforms"
            :key="platform.id"
            type="button"
            class="bz-tile"
            :class="{ 'is-selected': selectedPlatform && selectedPlatform.id === platform.id }"
            :data-testid="`platform-${platform.id}`"
            :style="{ '--platform-color': platform.color }"
            @click="selectPlatform(platform)"
          >
            <span v-if="platform.emoji" class="manual-order__logo manual-order__logo--emoji">{{ platform.emoji }}</span>
            <img v-else :src="getPlatformIcon(platform.id)" :alt="platform.id" class="manual-order__logo" />
            <span class="bz-tile__label">{{ displayLabel(platform) }}</span>
          </button>
        </div>
      </template>

      <!-- Step 2: code input -->
      <template v-else-if="step === 'input'">
        <div class="manual-order__platform">
          <span v-if="selectedPlatform.emoji" class="manual-order__logo manual-order__logo--emoji" :style="{ '--platform-color': selectedPlatform.color }">{{ selectedPlatform.emoji }}</span>
          <img v-else :src="getPlatformIcon(selectedPlatform.id)" :alt="selectedPlatform.id" class="manual-order__logo" />
          <span id="manual-order-platform" class="manual-order__platform-name">{{ displayLabel(selectedPlatform) }}</span>
        </div>

        <div v-if="orderCodePrefixConfig" class="manual-order__prefix">
          <span class="manual-order__segment">{{ orderCodePrefixConfig.prefix }}</span>
          <span class="manual-order__segment manual-order__segment--suffix" :class="{ 'manual-order__segment--filled': isValid }">{{ orderCodeSuffixDisplay }}</span>
          <span v-if="orderCodePrefixConfig.appendStr" class="manual-order__segment">{{ orderCodePrefixConfig.appendStr }}</span>
        </div>

        <div class="manual-order__field">
          <input
            ref="codeInput"
            v-model="code"
            :inputmode="currentInputMode"
            :maxlength="inputMaxLength"
            :placeholder="inputPlaceholder"
            :aria-label="inputHint"
            :aria-invalid="showError ? 'true' : undefined"
            class="manual-order__code"
            :class="{ 'is-valid': isValid, 'is-error': showError }"
            data-testid="manual-order-code"
            autocomplete="off"
            autocorrect="off"
            autocapitalize="characters"
            spellcheck="false"
            @input="onInput"
            @keydown.enter="submitIfValid"
          />
          <BzButton
            v-if="selectedPlatform.id === 'OWN_DELIVERY' && !orderCodePrefixConfig"
            class="manual-order__keyboard"
            kind="secondary"
            size="lg"
            :label="ownDeliveryNumeric ? 'ABC' : '123'"
            @click="toggleOwnDeliveryInputMode"
          />
        </div>
        <div v-if="orderCodePrefixConfig" class="manual-order__count">
          <span :class="{ 'manual-order__count--complete': code.length === orderCodePrefixConfig.suffixLength }">{{ code.length }}</span>
          <span>/</span>
          <span>{{ orderCodePrefixConfig.suffixLength }}</span>
        </div>
        <div v-else-if="!selectedPlatform.freeFormat" class="manual-order__count">
          <span :class="{ 'manual-order__count--complete': code.length === selectedPlatform.maxLength }">{{ code.length }}</span>
          <span>/</span>
          <span>{{ selectedPlatform.maxLength }}</span>
        </div>
        <p v-if="showError" class="manual-order__error" role="alert">
          <BzIcon name="error" />{{ $t('message.manualOrderInvalidCode') }}
        </p>
      </template>

      <!-- Step 3: sending -->
      <div v-else-if="step === 'sending'" class="bz-empty">
        <BzIcon name="progress_activity" class="bz-empty__icon bz-spinner" />
        <p id="manual-order-sending" class="bz-empty__title" role="status">{{ $t('message.manualOrderCreating') }}</p>
      </div>
    </div>

    <template v-if="step !== 'sending'" #footer>
      <div class="manual-order__actions">
        <!-- The one primary action, only when the code has no fixed length and so is not sent by itself. -->
        <BzButton
          v-if="step === 'input' && selectedPlatform.freeFormat && !orderCodePrefixConfig && isValid"
          class="manual-order__accept"
          kind="primary"
          size="lg"
          block
          :label="$t('message.accept')"
          @click="submit"
        />
        <div class="manual-order__pair">
          <BzButton
            v-if="step === 'input'"
            kind="secondary"
            size="md"
            icon="arrow_back"
            :label="$t('message.back')"
            @click="goToPlatforms"
          />
          <BzButton
            kind="secondary"
            size="md"
            :label="$t('message.cancel')"
            data-testid="manual-order-cancel"
            @click="cancel"
          />
        </div>
      </div>
    </template>
  </BzDialog>
</template>

<script>
import { fetchOwnDeliveryCodePresets, fetchDeliveryPlatformsForCurrentDevice } from '@/graphql';
import Logger from '@/logger';
import BzButton from '@/ui/components/BzButton.vue';
import BzDialog from '@/ui/components/BzDialog.vue';
import BzIcon from '@/ui/components/BzIcon.vue';
import { platformName } from '@/platformNames';

const logger = new Logger({ screen: 'ManualOrderModal' });

// Presentation-only assets per platform id (brand colour + optional emoji; the names live in
// src/platformNames.js, shared with the rest of the app). NONE of the business rules (which
// platforms exist, code length, validation) live here — those come 100% from the backend
// (deliveryPlatformsForCurrentDevice). Unknown ids fall back to a generic look (OTHERS icon +
// neutral colour + a name readable from the id).
// The colours are tokens (src/ui/tokens.css) and only ever reach CSS, as `--platform-color`.
const PLATFORM_PRESENTATION = {
  GLOVO:        { color: 'var(--bz-platform-glovo)' },
  JUSTEAT:      { color: 'var(--bz-platform-just-eat)' },
  UBER:         { color: 'var(--bz-platform-uber-eats)' },
  DELIVEROO:    { color: 'var(--bz-platform-deliveroo)' },
  OWN_DELIVERY: { color: 'var(--bz-platform-own-delivery)', emoji: '📦' },
  TAKE_AWAY:    { color: 'var(--bz-platform-take-away)', emoji: '🛍️' },
};

// Display order for the selector: known platforms first in this order, unknown ones appended in
// backend order. Purely cosmetic.
const PLATFORM_ORDER = ['GLOVO', 'UBER', 'JUSTEAT', 'DELIVEROO', 'OWN_DELIVERY', 'TAKE_AWAY'];

// Total expected code length from a backend regex like "\b\d{12}\b" — used for the own-delivery
// prefix math when the platform rule itself doesn't carry a single fixed length.
function totalLengthFromRegex(regex) {
  const m = /\{(\d+)\}/.exec(regex || '');
  return m ? parseInt(m[1], 10) : null;
}

// Turn a backend code regex (the platform's deliveryIdRegex — e.g. Uber "\b[A-F0-9]{5}\b",
// Glovo "\b\d{12}\b", JustEat "\b\d{9}\b" — or the ticketIdRegex fallback for channels with no
// aggregator id) into the concrete input rule the modal needs: an anchored validation RegExp +
// length + char class. Every field is DERIVED from the backend pattern — nothing is hardcoded.
function parseCodeRule(codeRegex) {
  const raw = (codeRegex || '').trim();
  // No usable pattern → free-format: accept any non-empty code, no fixed length.
  if (!raw || raw === '(?!)') {
    return { validationRegex: null, minLength: 1, maxLength: null, isNumeric: false, isHex: false, freeFormat: true };
  }
  // Strip word-boundary / anchor wrappers to get the core body, then re-anchor for full-match.
  const body = raw
    .replace(/^\^/, '').replace(/\$$/, '')
    .replace(/^\\b/, '').replace(/\\b$/, '');

  // Char class: drop zero-width \b assertions first (they're not characters — e.g. an alternation
  // like "\d{4}\b|\b\d{19}") so internal boundaries don't pollute the test. Numeric only if it uses
  // \d and no letter ranges; hex if it references A-F.
  const charBody = body.replace(/\\b/g, '');
  const lettersOutsideDigits = charBody.replace(/\\d/g, '');
  const isHex = /A-F/i.test(lettersOutsideDigits);
  const isNumeric = /\\d/.test(charBody) && !/[A-Za-z]/.test(lettersOutsideDigits);

  // Anchored validator built verbatim from the backend body. Case-insensitive for hex codes
  // (ticket hex codes are case-insensitive and the input is force-uppercased on entry, so a
  // lowercase backend class like [a-f0-9] still validates).
  let validationRegex = null;
  try { validationRegex = new RegExp('^(?:' + body + ')$', isHex ? 'i' : ''); } catch (e) { validationRegex = null; }

  // Length derivation: ONLY trust a fixed length when the body is a SINGLE atom (\d or a char
  // class) followed by a SINGLE quantifier and nothing else. Patterns with literals or multiple
  // segments (e.g. "H-\d{4}", "tnum=(\b\d{8}\b)") carry length the {n} doesn't capture, so we fall
  // back to free-format and rely purely on the anchored validationRegex (the input cap then
  // defaults to a generous max). This keeps every length rule honest to the backend pattern.
  let minLength = 1, maxLength = null;
  const simple = /^(?:\\d|\[[^\]]+\])\{(\d+)(?:,(\d+))?\}$/.exec(body);
  if (simple) {
    minLength = parseInt(simple[1], 10);
    maxLength = simple[2] != null ? parseInt(simple[2], 10) : minLength;
  }

  return {
    validationRegex,
    minLength,
    maxLength,
    isNumeric,
    isHex,
    // freeFormat = the user can't rely on a single fixed length to know when they're done
    // (variable-length range, literals, or unparseable) → explicit confirm button, no auto-submit.
    freeFormat: maxLength == null || minLength !== maxLength,
  };
}

export default {
  name: 'ManualOrderModal',
  components: { BzButton, BzDialog, BzIcon },
  emits: ['cancel', 'order-created'],
  props: {
    show: {
      type: Boolean,
      default: false
    },
    restaurantId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      step: 'platform',
      selectedPlatform: null,
      code: '',
      showError: false,
      ownDeliveryNumeric: true,
      isMobile: window.innerWidth < 768,
      // Backend-resolved own-delivery presets for this restaurant (null = no own delivery).
      // {prepends:[], appends:[], regex, expiration} — used ONLY to compose the OWN_DELIVERY prefix
      // (store id + today's server-side date). The OWN_DELIVERY availability itself comes from the
      // platform list below.
      ownDeliveryPresets: null,
      // Platforms for this restaurant, resolved by the backend (incl. franchise inheritance) and
      // normalised into selector entries. Empty until loaded; never hardcoded.
      backendPlatforms: [],
      loadingPlatforms: false,
      loadError: false,
    };
  },
  computed: {
    // The dialog's subtitle: the question of the platform step, and the code step's instruction. Sending
    // says it in the body.
    subtitle() {
      if (this.step === 'platform') return this.$t('message.manualOrderSelectPlatform');
      if (this.step === 'input') return this.inputHint;
      return '';
    },
    // What the code step asks for, by the rule the backend sent for the platform.
    inputHint() {
      const sp = this.selectedPlatform;
      if (!sp) return '';
      if (this.orderCodePrefixConfig) return this.$t('message.manualOrderEnterOrderSuffixN', { n: this.orderCodePrefixConfig.suffixLength });
      if (sp.freeFormat) return this.$t('message.manualOrderEnterCode');
      if (sp.isNumeric) return this.$t('message.manualOrderEnterCodeN', { n: sp.maxLength });
      return this.$t('message.manualOrderEnterCodeCharsN', { n: sp.maxLength });
    },
    availablePlatforms() {
      return this.backendPlatforms;
    },
    // A third column on a short landscape screen only pays off from five platforms: up to four, two
    // columns already fit (two rows), and three would leave one alone on its row. Only on this step:
    // the code step is short, and its field has no reason to grow.
    wideGrid() {
      return this.step === 'platform' && this.availablePlatforms.length >= 5;
    },
    // OWN_DELIVERY code composition from the backend presets: a fixed prefix (prepends joined =
    // store id + today's server-side date) + the user-typed suffix + a fixed append. The total
    // length comes from the platform's own ticket rule (fallback: the presets regex). null => no
    // prefix (free-format input — e.g. presets missing).
    orderCodePrefixConfig() {
      if (this.selectedPlatform?.id !== 'OWN_DELIVERY') return null;
      const p = this.ownDeliveryPresets;
      if (!p || !Array.isArray(p.prepends) || p.prepends.length === 0) return null;
      const prefix = p.prepends.join('');
      const appendStr = Array.isArray(p.appends) ? p.appends.join('') : '';
      const total = this.selectedPlatform.maxLength || totalLengthFromRegex(p.regex);
      const suffixLength = total ? total - prefix.length - appendStr.length : null;
      if (!suffixLength || suffixLength <= 0) return null;
      // Validate the FULL composed code with an anchored regex: prefer the platform's own derived
      // rule; fall back to the presets regex anchored the SAME way (not a loose substring match).
      const regex = this.selectedPlatform.validationRegex || parseCodeRule(p.regex).validationRegex;
      return { prefix, appendStr, suffixLength, regex };
    },
    orderCodeSuffixDisplay() {
      if (!this.orderCodePrefixConfig) return '';
      return this.code.padEnd(this.orderCodePrefixConfig.suffixLength, '_');
    },
    isValid() {
      if (!this.selectedPlatform || !this.code) return false;
      const cfg = this.orderCodePrefixConfig;
      if (cfg) {
        if (this.code.length !== cfg.suffixLength) return false;
        const full = cfg.prefix + this.code + cfg.appendStr;
        if (!cfg.regex) return true;
        try { return cfg.regex.test(full); } catch (e) { return true; }
      }
      const rx = this.selectedPlatform.validationRegex;
      if (!rx) return this.code.length >= (this.selectedPlatform.minLength || 1); // free-format
      try { return rx.test(this.code); } catch (e) { return true; }
    },
    // Placeholder derived from the rule (no per-platform hardcoded sample codes).
    inputPlaceholder() {
      const sp = this.selectedPlatform;
      if (!sp) return '';
      if (this.orderCodePrefixConfig) return '0'.repeat(this.orderCodePrefixConfig.suffixLength);
      const n = sp.maxLength ? Math.min(sp.maxLength, 12) : 6;
      return (sp.isNumeric ? '0' : '·').repeat(n);
    },
    inputMaxLength() {
      if (this.orderCodePrefixConfig) return this.orderCodePrefixConfig.suffixLength;
      return this.selectedPlatform?.maxLength || 40;
    },
    currentInputMode() {
      if (this.orderCodePrefixConfig) return 'numeric';
      if (this.selectedPlatform?.id === 'OWN_DELIVERY') {
        return this.ownDeliveryNumeric ? 'numeric' : 'text';
      }
      return this.selectedPlatform?.isNumeric ? 'numeric' : 'text';
    }
  },
  watch: {
    show(val) {
      if (val) {
        this.step = 'platform';
        this.code = '';
        this.selectedPlatform = null;
        this.showError = false;
        // Refetch on every open so the platform list + own-delivery date (server-side) always
        // reflect the current backend config.
        this.loadPlatforms();
      }
    }
  },
  methods: {
    displayLabel(p) {
      // The same name the rest of the app gives it (src/platformNames.js).
      return p ? platformName(p.id, (key) => this.$t(key)) : '';
    },
    getPlatformIcon(platform) {
      try {
        return require(`@/assets/delivery_platforms/${platform}.png`);
      } catch {
        return require(`@/assets/delivery_platforms/OTHERS.png`);
      }
    },
    // Normalise the backend rows into selector entries. A manual order stores the typed code as the
    // platform delivery_id and the backend matches it by EXACT delivery_id, so we validate against
    // deliveryIdRegex (Uber 5-hex, Glovo 12 digits, JustEat 9). Channels with no aggregator id
    // (OWN_DELIVERY / TAKE_AWAY → deliveryIdRegex "(?!)") fall back to ticketIdRegex, the internal
    // code (e.g. the composed 12-digit own-delivery code). Drop skipped platforms; attach
    // presentation; order cosmetically.
    normalizePlatforms(rows) {
      if (!Array.isArray(rows)) return [];
      const built = rows
        .filter(r => r && r.deliveryPlatform && !r.skipOrders)
        .map(r => {
          const id = r.deliveryPlatform;
          const pres = PLATFORM_PRESENTATION[id] || {};
          const deliveryIdRegex = r.deliveryPlatformByDeliveryPlatform && r.deliveryPlatformByDeliveryPlatform.deliveryIdRegex;
          // Prefer the platform delivery_id shape; "(?!)" (never-match) means the platform has no
          // aggregator id → use the per-client ticket code instead.
          const hasDeliveryId = deliveryIdRegex && deliveryIdRegex !== '(?!)';
          const codeRegex = hasDeliveryId ? deliveryIdRegex : r.ticketIdRegex;
          const rule = parseCodeRule(codeRegex);
          return {
            id,
            emoji: pres.emoji || null,
            color: pres.color || 'var(--bz-platform-other)',
            codeRegex: codeRegex || null,
            ...rule,
          };
        });
      built.sort((a, b) => {
        const ia = PLATFORM_ORDER.indexOf(a.id);
        const ib = PLATFORM_ORDER.indexOf(b.id);
        return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
      });
      return built;
    },
    async loadPlatforms() {
      // Only block with a spinner on a COLD load (nothing cached yet). With data already in hand
      // (warm open, TTL not necessarily expired) we render instantly and let the cached fetch
      // revalidate in the background — no flashing "Cargando plataformas..." on every open.
      const cold = this.backendPlatforms.length === 0;
      if (cold) {
        this.loadingPlatforms = true;
        this.loadError = false;
      }
      try {
        const [rows, presets] = await Promise.all([
          fetchDeliveryPlatformsForCurrentDevice(),
          fetchOwnDeliveryCodePresets(),
        ]);
        this.ownDeliveryPresets = presets;
        this.backendPlatforms = this.normalizePlatforms(rows);
        this.loadError = false;
        // Telemetry: OWN_DELIVERY offered by the backend list but with no usable prefix presets is
        // a backend config inconsistency (platform configured without a store_id for the prepend
        // lambda). The list is authoritative per contract so we still show it, but it degrades to
        // full manual code entry — flag it so we can catch the misconfig.
        const hasOwn = this.backendPlatforms.some(p => p.id === 'OWN_DELIVERY');
        const hasPrefix = !!(presets && Array.isArray(presets.prepends) && presets.prepends.length);
        if (hasOwn && !hasPrefix) {
          logger.warning('OWN_DELIVERY listed but no own-delivery prefix presets; falling back to full manual code entry', { restaurantId: this.restaurantId });
        }
      } catch (e) {
        // On a warm open keep showing the data we already have; only surface the error state when
        // we had nothing to show (cold load failed).
        if (cold) {
          this.loadError = true;
          this.backendPlatforms = [];
          this.ownDeliveryPresets = null;
        }
      } finally {
        this.loadingPlatforms = false;
      }
    },
    selectPlatform(platform) {
      this.selectedPlatform = platform;
      this.code = '';
      this.showError = false;
      this.ownDeliveryNumeric = true;
      this.step = 'input';
      this.$nextTick(() => {
        this.$refs.codeInput?.focus();
      });
    },
    toggleOwnDeliveryInputMode() {
      this.ownDeliveryNumeric = !this.ownDeliveryNumeric;
      this.$nextTick(() => {
        this.$refs.codeInput?.focus();
      });
    },
    onInput() {
      this.showError = false;
      const sp = this.selectedPlatform;

      // Char filtering derived from the backend rule (not from the platform id).
      if (sp.isHex) {
        this.code = this.code.toUpperCase().replace(/[^A-F0-9]/g, '');
      } else if (this.currentInputMode === 'numeric') {
        this.code = this.code.replace(/\D/g, '');
      }

      // Prefixed OWN_DELIVERY: auto-submit when the user-typed suffix is complete
      if (this.orderCodePrefixConfig) {
        if (this.code.length === this.orderCodePrefixConfig.suffixLength) {
          if (this.isValid) this.submit();
          else this.showError = true;
        }
        return;
      }

      // Auto-submit when the fixed length is reached (only for single-length, non-free formats)
      if (!sp.freeFormat && sp.maxLength && this.code.length === sp.maxLength) {
        if (this.isValid) {
          this.submit();
        } else {
          this.showError = true;
        }
      }
    },
    submitIfValid() {
      if (this.isValid) {
        this.submit();
      } else if (this.code.length > 0) {
        this.showError = true;
      }
    },
    async submit() {
      if (this.step === 'sending') return; // idempotency guard: never emit a second create-order
      this.step = 'sending';
      const cfg = this.orderCodePrefixConfig;
      const finalCode = cfg ? cfg.prefix + this.code + cfg.appendStr : this.code;
      this.$emit('order-created', {
        deliveryId: finalCode,
        deliveryPlatform: this.selectedPlatform.id,
        restaurantId: this.restaurantId
      });
    },
    cancel() {
      this.$emit('cancel');
    },
    goToPlatforms() {
      this.step = 'platform';
      this.code = '';
    },
    // Back (and Escape) on the code step goes back to the platforms, as «Atrás» does; on the others it
    // closes, as «Cancelar» does.
    // The X and back go a step back from the code to the platforms; a tap outside the card leaves the whole
    // dialog, as it does everywhere else.
    onDismiss(reason) {
      if (this.step === 'input' && reason !== 'outside') {
        this.goToPlatforms();
      } else {
        this.cancel();
      }
    },
  }
};
</script>

<style scoped>
.manual-order {
  display: flex;
  flex-direction: column;
  gap: var(--bz-space-3);
}

/* The platforms: two to a row, each one a tile of the system (stacked: its logo above, its name below).
   Grid, not one flex row: the backend can send 5-6 platforms, and two columns keep every name readable
   whatever the count. */
.manual-order__platforms {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: var(--bz-target-gap);
}

/* A landscape screen too short for the two-column grid has width to spare: a third column takes a row
   off before anything has to scroll (`wideGrid`: five platforms or more, on the platform step). Same
   short-screen line as the help flow's (700 px); the dialog's width is in the second style block. */
@media (orientation: landscape) and (max-height: 700px) and (min-width: 768px) {
  .manual-order__platforms--wide {
    grid-template-columns: repeat(3, 1fr);
  }
}

/* The tile is a button: it keeps its own face (the app's, not the browser's), and a name that is longer
   than its column wraps instead of overflowing. */
.manual-order__platforms .bz-tile {
  min-width: 0;
  cursor: pointer;
}

.manual-order__platforms .bz-tile__label {
  min-width: 0;
  overflow-wrap: anywhere;
}

/* The platform's logo as it is, at 48 inside its own square; a platform that has an emoji instead gets
   the platform's colour behind it. */
.manual-order__logo {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  width: var(--bz-target-min);
  height: var(--bz-target-min);
  border-radius: var(--bz-radius-md);
  object-fit: contain;
}

.manual-order__logo--emoji {
  background: var(--platform-color, var(--bz-platform-other));
  font-size: var(--bz-icon-md);
}

/* Step 2: the platform it is for, then the code. */
.manual-order__platform {
  display: flex;
  align-items: center;
  gap: var(--bz-space-3);
}

.manual-order__platform-name {
  font-size: var(--bz-text-lg);
  line-height: var(--bz-leading-lg);
  letter-spacing: var(--bz-tracking-lg);
  font-weight: var(--bz-weight-label);
}

/* An own-delivery code is the store and the day (fixed) and what the cook types (the suffix). */
.manual-order__prefix {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--bz-space-2);
}

.manual-order__segment {
  display: inline-flex;
  align-items: center;
  min-height: var(--bz-target-min);
  box-sizing: border-box;
  padding: 0 var(--bz-space-4);
  border-radius: var(--bz-radius-md);
  background: var(--bz-control);
  color: var(--bz-ink-2);
  /* The pieces of the code are the code: its second step, 28 at 700, the input's own size. */
  font-size: var(--bz-text-3xl);
  line-height: var(--bz-leading-3xl);
  letter-spacing: var(--bz-tracking-3xl);
  font-weight: var(--bz-weight-strong);
  font-variant-numeric: tabular-nums;
}

.manual-order__segment--suffix {
  background: var(--bz-action-container);
  color: var(--bz-ink);
}

.manual-order__segment--filled {
  background: var(--bz-ok-container);
  color: var(--bz-on-ok-container);
}

.manual-order__field {
  display: flex;
  align-items: stretch;
  gap: var(--bz-target-gap);
}

/* The code is read one character at a time, at arm's length: 72 high, the title's size and up, the
   control grey with the hairline; its state is the edge (a ring in the focus blue when it has the
   keyboard, the ok green when it is complete, the wrong orange when it is not a code). The state is also
   said in words below (the count, the error). */
.manual-order__code {
  flex: 1 1 auto;
  min-width: 0;
  box-sizing: border-box;
  height: var(--bz-target-primary);
  padding: 0 var(--bz-space-4);
  border: 2px solid var(--bz-edge);
  border-radius: var(--bz-radius-lg);
  background: var(--bz-control);
  color: var(--bz-ink);
  caret-color: var(--bz-action-ink);
  font-family: var(--bz-font);
  font-size: var(--bz-text-3xl);
  line-height: var(--bz-leading-3xl);
  letter-spacing: var(--bz-tracking-3xl);
  font-weight: var(--bz-weight-strong);
  font-variant-numeric: tabular-nums;
  text-align: center;
}

.manual-order__code::placeholder {
  color: var(--bz-ink-2);
  opacity: 0.6;
}

.manual-order__code:focus-visible {
  outline: 3px solid var(--bz-focus);
  outline-offset: 2px;
}

.manual-order__code.is-valid {
  border-color: var(--bz-ok);
}

.manual-order__code.is-error {
  border-color: var(--bz-wrong);
}

.manual-order__keyboard {
  flex: 0 0 auto;
  min-width: var(--bz-target-primary);
}

.manual-order__count {
  display: flex;
  justify-content: flex-end;
  gap: var(--bz-space-1);
  font-size: var(--bz-text-sm);
  line-height: var(--bz-leading-sm);
  letter-spacing: var(--bz-tracking-sm);
  font-weight: var(--bz-weight-body);
  color: var(--bz-ink-2);
  font-variant-numeric: tabular-nums;
}

.manual-order__count--complete {
  color: var(--bz-ok);
}

.manual-order__error {
  display: flex;
  align-items: center;
  gap: var(--bz-space-2);
  margin: 0;
  font-size: var(--bz-text-md);
  line-height: var(--bz-leading-md);
  letter-spacing: var(--bz-tracking-md);
  font-weight: var(--bz-weight-body);
  color: var(--bz-wrong);
}

/* The footer: the primary action, when there is one, over «Atrás» and «Cancelar» side by side, all the
   dialog's width. */
.manual-order__actions {
  display: flex;
  flex-direction: column;
  gap: var(--bz-target-gap);
  width: 100%;
}

.manual-order__pair {
  display: grid;
  grid-auto-columns: 1fr;
  grid-auto-flow: column;
  gap: var(--bz-target-gap);
}
</style>

<style>
/* The dialog's own width, which scoped CSS cannot reach (it is teleported to <body>): three columns of
   tiles need more than the system's 560, and only on the same short landscape screens that take the third
   column (the platform step with five platforms or more — `wideGrid`). The code step keeps the 560. */
@media (orientation: landscape) and (max-height: 700px) and (min-width: 768px) {
  .bz-dialog--card.p-dialog.manual-order-dialog--wide {
    width: min(640px, calc(100vw - 2 * var(--bz-space-4)));
  }
}
</style>
